import React from 'react';
import clsx from 'clsx';
import {faTimes} from '@netx/core-icons';
import {
	Divider,
	type AlertColor,
	DialogTitle as MuiDialogTitle,
	type DialogTitleProps as MuiDialogTitleProps,
} from '@mui/material';
import IconButton from '@netx/core-components/IconButton';
import {
	composeClasses,
	styled,
	type WithClasses,
	type WithMuiPropOverrides,
} from '@netx/core-components/Theme';
import {i18n} from '@netx/core-next/locales/i18n-instance';
import {Title, type TitleProps} from '../Title';
import {type ReactAction} from '@netx/core-bootstrap';
import {ActionList} from '@netx/core-components/ActionList';
import {
	DIALOG_TITLE_CLASS_NAME,
	dialogTitleClasses,
	type DialogTitleClasses,
	getDialogTitleUtilityClass,
} from './dialogTitleClasses';

type DialogTitleOwnerProps = WithClasses<
	DialogTitleClasses,
	{
		hasIcon?: boolean;
	}
>;

const useUtilityClasses = (ownerState: DialogTitleOwnerProps) => {
	const {classes, hasIcon} = ownerState;
	const slots = {
		root: ['root', hasIcon && 'hasIcon'],
		actions: ['actions'],
		controls: ['controls'],
		icon: ['icon'],
		title: ['title'],
	};
	return composeClasses(slots, getDialogTitleUtilityClass, classes);
};

const DialogTitleRoot = styled(MuiDialogTitle, {
	name: DIALOG_TITLE_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	alignItems: 'center',
	overflow: 'hidden',
	[`&.${dialogTitleClasses.hasIcon}`]: {
		paddingLeft: theme.spacing(2),
	},
}));

const DialogTitleText = styled(Title, {
	name: DIALOG_TITLE_CLASS_NAME,
	slot: 'Title',
})(({theme}) => ({
	flex: '1 1 auto',
	overflow: 'hidden',
}));

const DialogTitleActions = styled('div', {
	name: DIALOG_TITLE_CLASS_NAME,
	slot: 'Title',
})(({theme}) => ({
	flex: '0 0 auto',
	marginLeft: theme.spacing(1),
}));

const DialogTitleControls = styled('div', {
	name: DIALOG_TITLE_CLASS_NAME,
	slot: 'Controls',
})(({theme}) => ({}));

export type DialogTitleProp =
	| string
	| {
			primary: string;
			secondary?: string;
	  };

export interface DialogTitleProps
	extends
		Omit<WithMuiPropOverrides<MuiDialogTitleProps>, 'classes' | 'variant'>,
		Omit<TitleProps, 'classes'>,
		WithClasses<
			any,
			{
				disabled?: boolean;
				onClose?: () => void;
				controls?: React.ReactNode;
				actions?: Array<ReactAction | ReactAction[]>;
				actionsDisabled?: boolean;
				severity?: AlertColor;
				TitleProps?: Partial<TitleProps>;
			}
		> {}

const DialogTitleInner = ({
	classes: _classes,
	className,
	actions,
	actionsDisabled,
	actionButton,
	controls,
	disabled,
	icon,
	loading,
	primary,
	secondary,
	severity,
	onClose,
	PrimaryTitleProps,
	SecondaryTitleProps,
	TitleProps,
	...props
}: DialogTitleProps) => {
	const ownerState: DialogTitleOwnerProps = {
		classes: _classes,
		className,
		hasIcon: icon != null,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<DialogTitleRoot {...props} className={clsx(classes.root, className)}>
			<div
				style={{
					display: 'flex',
					flex: '1 1 auto',
					alignItems: 'center',
					overflow: 'hidden',
				}}
			>
				<DialogTitleText
					data-testid="dialog-title-text"
					actionButton={actionButton}
					icon={icon}
					loading={loading}
					primary={primary}
					secondary={secondary}
					severity={severity}
					PrimaryTitleProps={{
						variant: 'h6',
						...PrimaryTitleProps,
					}}
					SecondaryTitleProps={SecondaryTitleProps}
					{...TitleProps}
					sx={{
						flex: actions ? '0 1 auto' : undefined,
						...TitleProps?.sx,
					}}
				/>
				{!!actions && actions.length > 0 && (
					<>
						<Divider orientation="vertical" flexItem sx={{ml: 2, mr: 0.5}} />
						<div
							style={{flex: '0 0 auto', display: 'flex', alignItems: 'center'}}
						>
							<ActionList
								disabled={disabled || loading || actionsDisabled}
								loading={loading}
								actions={actions}
							/>
						</div>
					</>
				)}
			</div>
			{controls ? (
				<DialogTitleControls
					data-testid="dialog-title-controls"
					className={classes.controls}
				>
					{controls}
				</DialogTitleControls>
			) : null}
			{onClose ? (
				<DialogTitleActions
					data-testid="dialog-title-actions"
					className={classes.actions}
				>
					<IconButton
						data-testid="dialog-title-close"
						disabled={disabled}
						label={i18n.t('i18n.close')}
						edge="end"
						icon={faTimes}
						color="inherit"
						onClick={onClose}
					/>
				</DialogTitleActions>
			) : null}
		</DialogTitleRoot>
	);
};

export const DialogTitlePrimitive = DialogTitleInner;

export const DialogTitle = DialogTitlePrimitive;

export default DialogTitle;
